import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import AdminLayout, { AdminPageHeader, AdminTable } from "./layout";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

interface SupplyCompany {
  id: number;
  name: string;
  type: string;
  description: string | null;
  website: string | null;
  mainPhone: string | null;
  mainEmail: string | null;
  nurseLinePhone: string | null;
  nurseLineHours: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
}

type SCForm = Omit<SupplyCompany, "id" | "createdAt">;

const EMPTY: SCForm = {
  name: "", type: "homecare", description: "", website: "", mainPhone: "", mainEmail: "",
  nurseLinePhone: "", nurseLineHours: "", notes: "", isActive: true,
};

async function apiFetch(path: string, opts?: RequestInit) {
  const res = await fetch(`${basePath}${path}`, { credentials: "include", ...opts });
  if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error((b as { error?: string }).error ?? "Request failed"); }
  return res.json();
}

function SCForm({ value, onChange }: { value: SCForm; onChange: (v: SCForm) => void }) {
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Company name *</Label>
          <Input value={value.name} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <Label>Type *</Label>
          <Select value={value.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="homecare">Homecare</SelectItem>
              <SelectItem value="pharmacy">Pharmacy</SelectItem>
              <SelectItem value="logistics">Logistics</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea rows={2} value={value.description ?? ""} onChange={(e) => set("description", e.target.value)} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Website</Label>
          <Input value={value.website ?? ""} onChange={(e) => set("website", e.target.value)} type="url" />
        </div>
        <div>
          <Label>Main email</Label>
          <Input value={value.mainEmail ?? ""} onChange={(e) => set("mainEmail", e.target.value)} type="email" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Main phone</Label>
          <Input value={value.mainPhone ?? ""} onChange={(e) => set("mainPhone", e.target.value)} />
        </div>
        <div>
          <Label>Nurse line phone</Label>
          <Input value={value.nurseLinePhone ?? ""} onChange={(e) => set("nurseLinePhone", e.target.value)} />
        </div>
      </div>
      <div>
        <Label>Nurse line hours</Label>
        <Input value={value.nurseLineHours ?? ""} onChange={(e) => set("nurseLineHours", e.target.value)} placeholder="Mon–Fri 8am–6pm" />
      </div>
      <div>
        <Label>Notes</Label>
        <Textarea rows={2} value={value.notes ?? ""} onChange={(e) => set("notes", e.target.value)} />
      </div>
      <div className="flex items-center gap-3">
        <Switch checked={value.isActive} onCheckedChange={(v) => set("isActive", v)} />
        <Label>Active</Label>
      </div>
    </div>
  );
}

export default function AdminSupplyCompanies() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState<SupplyCompany | null>(null);
  const [form, setForm] = useState<SCForm>(EMPTY);

  const { data: companies = [], isLoading } = useQuery<SupplyCompany[]>({
    queryKey: ["admin", "supply-companies"],
    queryFn: () => apiFetch("/api/admin/content/supply-companies"),
  });

  const saveMutation = useMutation({
    mutationFn: (body: SCForm) =>
      editing
        ? apiFetch(`/api/admin/content/supply-companies/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
        : apiFetch("/api/admin/content/supply-companies", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "supply-companies"] }); setDialogOpen(false); toast({ title: editing ? "Company updated" : "Company created" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/admin/content/supply-companies/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "supply-companies"] }); toast({ title: "Company deleted" }); },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  function openCreate() { setEditing(null); setForm(EMPTY); setDialogOpen(true); }
  function openEdit(c: SupplyCompany) {
    setEditing(c);
    setForm({ name: c.name, type: c.type, description: c.description, website: c.website, mainPhone: c.mainPhone, mainEmail: c.mainEmail, nurseLinePhone: c.nurseLinePhone, nurseLineHours: c.nurseLineHours, notes: c.notes, isActive: c.isActive });
    setDialogOpen(true);
  }

  const typeColors: Record<string, string> = { homecare: "bg-blue-100 text-blue-800", pharmacy: "bg-purple-100 text-purple-800", logistics: "bg-orange-100 text-orange-800", other: "bg-slate-100 text-slate-700" };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Supply Companies"
        description={`${companies.length} compan${companies.length !== 1 ? "ies" : "y"}`}
        action={<Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New company</Button>}
      />

      {isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : (
        <AdminTable
          columns={["Name", "Type", "Contact", "Status", "Actions"]}
          rows={companies.map((c) => [
            <p key="name" className="font-medium text-slate-900">{c.name}</p>,
            <span key="type" className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${typeColors[c.type] ?? "bg-slate-100 text-slate-700"}`}>{c.type}</span>,
            <div key="contact" className="text-xs text-slate-500"><p>{c.mainPhone}</p><p>{c.mainEmail}</p></div>,
            <span key="status" className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.isActive ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>{c.isActive ? "Active" : "Inactive"}</span>,
            <div key="actions" className="flex items-center gap-2">
              <Button size="icon" variant="ghost" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => setDeleteId(c.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>,
          ])}
        />
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Edit company" : "New supply company"}</DialogTitle></DialogHeader>
          <SCForm value={form} onChange={setForm} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate(form)} disabled={saveMutation.isPending}>
              {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete company?</AlertDialogTitle><AlertDialogDescription>This will permanently remove the supply company.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
